import type { WorkBook } from 'xlsx';
import { as400DivisionCode, type RdLocationMatch } from '@erp/rpc';

import { findHeaderRow, rowCell } from './cells';
import { rdLocationFlagReasons } from './rdLocationFlagReasons';
import type { FlaggedRdStockRow } from './types';
import { collectSheetAnalyses } from './workbookSheets';

export interface RdStockAnalysisBuckets {
  rowsScanned: number;
  mapped: FlaggedRdStockRow[];
  flagged: FlaggedRdStockRow[];
}

export interface RdStockSheetRow {
  fileName: string;
  sheetName: string;
  sheetRow: number;
  headerRow: unknown[];
  row: unknown[];
  cell: (header: string) => string;
}

/** STOCK_V / STOCKS_V company+division that identify Rexel RD (Jasmine: R / D). */
export function isRdStockAccess(company: string, division: string): boolean {
  return as400DivisionCode(`${company}${division}`) === 'RD';
}

export function emptyRdStockBuckets(): RdStockAnalysisBuckets {
  return { rowsScanned: 0, mapped: [], flagged: [] };
}

export function bucketRdStockRow(
  buckets: RdStockAnalysisBuckets,
  row: FlaggedRdStockRow,
): void {
  if (row.correspondence === 'mapped') {
    buckets.mapped.push(row);
    return;
  }
  buckets.flagged.push(row);
}

export function mergeRdStockAnalyses(
  parts: readonly RdStockAnalysisBuckets[],
): RdStockAnalysisBuckets | null {
  if (parts.length === 0) return null;
  return {
    rowsScanned: parts.reduce((n, part) => n + part.rowsScanned, 0),
    mapped: parts.flatMap((part) => part.mapped),
    flagged: parts.flatMap((part) => part.flagged),
  };
}

export function flagNonRdStockRow(args: {
  fileName: string;
  sheetName: string;
  sheetRow: number;
  company: string;
  division: string;
  productNumber: string;
  stockRecordId: string;
  stockLocation: string;
  itemMasterLocation?: string;
}): FlaggedRdStockRow {
  return {
    fileName: args.fileName,
    sheetName: args.sheetName,
    sheetRow: args.sheetRow,
    company: args.company,
    division: args.division,
    productNumber: args.productNumber,
    stockRecordId: args.stockRecordId,
    stockLocation: args.stockLocation,
    itemMasterLocation: args.itemMasterLocation ?? '',
    dc: '',
    correspondence: 'unmatched',
    reasons: [
      {
        id: 'nonRdDivision',
        column: 'STOCK ACCESS DIVISION',
        value: `${args.company}/${args.division}`,
      },
    ],
  };
}

export function rdStockRowFromMatch(args: {
  fileName: string;
  sheetName: string;
  sheetRow: number;
  company: string;
  division: string;
  productNumber: string;
  stockRecordId: string;
  match: RdLocationMatch | null;
  fallbackStock: string;
  fallbackItem: string;
  itemColumn: string;
}): FlaggedRdStockRow {
  const { match } = args;
  if (match?.status === 'mapped') {
    return {
      fileName: args.fileName,
      sheetName: args.sheetName,
      sheetRow: args.sheetRow,
      company: args.company,
      division: args.division,
      productNumber: args.productNumber,
      stockRecordId: args.stockRecordId,
      stockLocation: match.stockLetter,
      itemMasterLocation: match.itemMasterLetter,
      dc: match.dc,
      correspondence: 'mapped',
      reasons: [],
    };
  }
  const stockLocation = match?.stockLetter ?? args.fallbackStock;
  const itemMasterLocation = match?.itemMasterLetter ?? args.fallbackItem;
  const reason = match?.reason ?? 'unmappedStockLocation';
  return {
    fileName: args.fileName,
    sheetName: args.sheetName,
    sheetRow: args.sheetRow,
    company: args.company,
    division: args.division,
    productNumber: args.productNumber,
    stockRecordId: args.stockRecordId,
    stockLocation,
    itemMasterLocation,
    dc: '',
    correspondence: 'unmatched',
    reasons: rdLocationFlagReasons(
      reason,
      stockLocation,
      itemMasterLocation,
      args.itemColumn,
    ),
  };
}

function scanRdStockSheet(args: {
  rows: unknown[][];
  fileName: string;
  sheetName: string;
  headers: readonly string[];
  classifyRow: (input: RdStockSheetRow) => FlaggedRdStockRow;
}): RdStockAnalysisBuckets | null {
  const headerAt = findHeaderRow(args.rows, args.headers);
  if (headerAt === -1) return null;
  const headerRow = args.rows[headerAt];
  if (!headerRow) return null;

  const buckets = emptyRdStockBuckets();
  for (let index = headerAt + 1; index < args.rows.length; index += 1) {
    const row = args.rows[index];
    if (!row) continue;
    const cell = rowCell(row, headerRow);
    if (cell('PRODUCT NUMBER') === '') continue;
    buckets.rowsScanned += 1;
    bucketRdStockRow(
      buckets,
      args.classifyRow({
        fileName: args.fileName,
        sheetName: args.sheetName,
        sheetRow: index + 1,
        headerRow,
        row,
        cell,
      }),
    );
  }
  return buckets;
}

/** Shared STOCK_V / STOCKS_V row: non-RD first, then the Vera match. */
export function classifyRdAccessRow(args: {
  input: RdStockSheetRow;
  match: RdLocationMatch | null;
  itemColumn: string;
  itemMasterLocation?: string;
}): FlaggedRdStockRow {
  const company = args.input.cell('STOCK ACCESS COMPANY');
  const division = args.input.cell('STOCK ACCESS DIVISION');
  const productNumber = args.input.cell('PRODUCT NUMBER');
  const stockRecordId = args.input.cell('STOCK RECORD ID');
  const stockLocation = args.input.cell('STOCK ACCESS LOCATION');
  if (!isRdStockAccess(company, division)) {
    return flagNonRdStockRow({
      fileName: args.input.fileName,
      sheetName: args.input.sheetName,
      sheetRow: args.input.sheetRow,
      company,
      division,
      productNumber,
      stockRecordId,
      stockLocation,
      itemMasterLocation: args.itemMasterLocation,
    });
  }
  return rdStockRowFromMatch({
    fileName: args.input.fileName,
    sheetName: args.input.sheetName,
    sheetRow: args.input.sheetRow,
    company,
    division,
    productNumber,
    stockRecordId,
    match: args.match,
    fallbackStock: stockLocation,
    fallbackItem: args.itemMasterLocation ?? '',
    itemColumn: args.itemColumn,
  });
}

/** Walk every tab, skip blank product rows, bucket mapped vs flagged. */
export function scanRdStockWorkbook(args: {
  workbook: WorkBook;
  fileName: string;
  headers: readonly string[];
  classifyRow: (input: RdStockSheetRow) => FlaggedRdStockRow;
}): RdStockAnalysisBuckets | null {
  return mergeRdStockAnalyses(
    collectSheetAnalyses(args.workbook, (rows, sheetName) =>
      scanRdStockSheet({
        rows,
        fileName: args.fileName,
        sheetName,
        headers: args.headers,
        classifyRow: args.classifyRow,
      }),
    ),
  );
}
